import axios from 'axios';
import { baseWindowsFromApi, windowsFromApi } from '@/utils/hourlyBudgetSchedule';
import type { HourSlotMap } from '@/utils/hourlyBudgetHourSlots';
import {
  expandWindowsToWeeklySlots,
  weeklyAmountsFromApi,
  type DayAmounts,
  type WeekdayIndex,
} from '@/utils/hourlyBudgetWeek';
import { formatCurrency } from '@/utils/formatCurrency';
import { validateCampaignBudget } from '@/utils/campaignCreate';
import type { SmartBudgetPlan } from '@/types/smartBudget';

export function weeklyTotalFromDaily(daily: number): number {
  return Math.round(daily * 7 * 100) / 100;
}

export function parseDailyBudgetInput(raw: string): { value: number | null; error?: string } {
  const trimmed = raw.trim();
  if (!trimmed) return { value: null, error: 'Enter a daily budget' };
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return { value: null, error: 'Daily budget must be a number' };
  const budgetError = validateCampaignBudget(parsed, 'SP');
  if (budgetError) return { value: null, error: budgetError };
  return { value: parsed };
}

export function formatSmartBudgetInr(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  const digits = Number.isInteger(rounded) ? 0 : 2;
  return formatCurrency(rounded, undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function smartBudgetErrorMessage(error: unknown, fallback = 'Could not build a smart budget'): string {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail;
    if (detail && typeof detail === 'object' && detail !== null && 'message' in detail) {
      const message = (detail as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim()) return message;
    }
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export function smartBudgetPreviewSchedule(
  payload: SmartBudgetPlan['hourly_budget_payload']
): {
  slotsByDay: Record<WeekdayIndex, HourSlotMap>;
  amountsByDay: Record<WeekdayIndex, DayAmounts>;
} {
  const row = payload as unknown as Record<string, unknown>;
  const hourly = payload.hourly_budget != null ? String(payload.hourly_budget) : '';
  const base = payload.base_budget != null ? String(payload.base_budget) : '';
  return {
    slotsByDay: expandWindowsToWeeklySlots(baseWindowsFromApi(row), windowsFromApi(row.time_windows)),
    amountsByDay: weeklyAmountsFromApi(payload.day_settings, hourly, base),
  };
}
